import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { RootState } from '../../store/store';
import { clearCotacaoData } from '../../store/cotacaoSlice';
import { iniciarNovaAdesao } from '../../utils/adesaoFluxo';
import { performLogout } from '../../utils/authLogout';
import { idOpcaoAtiva, OpcaoMenu, opcoesVisiveis } from '../../views/home/menuOpcoes';
import { HOME } from '../../views/home/routes';
import { LOGIN } from '../../views/auth/routes';
import SinoNovidades from '../sino-novidades/SinoNovidades';
import { rotaUsaShell } from '../shell/rotasComShell';

import logo from '../../img/logo.svg';
import './NavGlobal.css';

/** Classe no body que trava a rolagem enquanto o drawer está aberto. */
const CLASSE_TRAVA_ROLAGEM = 'nav-global-aberto';

/** Casinha do item "Início" — inline para não depender de asset novo em src/img. */
const IconeInicio: React.FC = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
    <path d="M12 3.2 3.5 10.4v10.1h6.1v-5.9h4.8v5.9h6.1V10.4z" fill="currentColor" />
  </svg>
);

/**
 * Navegação global (Plano D, at-32).
 *
 * Camada ADITIVA: montada uma única vez no `App`, fora do `<Routes>`, para não
 * mexer nos headers heterogêneos de cada tela (cada uma tem o seu, algumas com
 * "Voltar"). Eles continuam funcionando — o drawer é caminho adicional, não
 * substituto. Antes daqui, trocar de fluxo exigia voltar até a Home.
 *
 * As opções vêm de `views/home/menuOpcoes.ts` — a MESMA lista da Home, para as
 * duas nunca divergirem.
 */
const NavGlobal: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const accessToken = useSelector((state: RootState) => state.auth.accessToken);
  const userType = useSelector((state: RootState) => state.auth.userType);

  const [aberto, setAberto] = useState(false);
  // Altura do PendenciasBanner (faixa no topo, TI/ADMIN): o hambúrguer é
  // `position: fixed` e ficaria atrás dela. É medida, e não fixada no CSS,
  // porque o banner tem altura variável (uma linha por pendência) e nem sempre
  // existe.
  const [offsetTopo, setOffsetTopo] = useState(0);

  const botaoRef = useRef<HTMLButtonElement>(null);
  const fecharRef = useRef<HTMLButtonElement>(null);
  // Só devolve o foco ao hambúrguer se o drawer chegou a abrir — sem isso, o
  // efeito de foco rodaria na montagem e roubaria o foco em todo carregamento.
  const jaAbriu = useRef(false);

  const naTelaDeLogin = location.pathname === LOGIN();
  const visivel = !!accessToken && !naTelaDeLogin;
  /*
   * Rotas já migradas para o `AppShell` (at-55) trazem a própria navegação —
   * aqui o hambúrguer e o drawer se recolhem, senão a tela teria DOIS gatilhos
   * de menu. O SINO continua sendo montado por este componente em todas as
   * rotas: ele carrega polling e estado próprios, e reimplementá-lo no shell
   * seria duplicar comportamento testado sem ganho nenhum.
   */
  const shellAssumeNavegacao = rotaUsaShell(location.pathname);

  const opcoes = useMemo(() => opcoesVisiveis(userType), [userType]);
  const idAtivo = useMemo(
    () => idOpcaoAtiva(opcoes, location.pathname),
    [opcoes, location.pathname],
  );
  const inicioAtivo = location.pathname === HOME() || location.pathname === '/';

  const fechar = useCallback(() => setAberto(false), []);

  // Navegar fecha o drawer. Reage ao pathname (e não ao clique) para cobrir
  // também voltar/avançar do navegador com o drawer aberto.
  useEffect(() => {
    setAberto(false);
  }, [location.pathname]);

  // Esc fecha — no `document`, pois o foco pode estar em qualquer item.
  useEffect(() => {
    if (!aberto) return undefined;

    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') fechar();
    };

    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [aberto, fechar]);

  // Trava de rolagem. ⚠️ Quem rola neste app NÃO é o <body> (que já tem
  // `overflow: hidden` no CSS base) e sim o `.main-containt` de cada tela — a
  // classe no body é o gancho para o CSS travar os dois.
  useEffect(() => {
    if (!aberto) return undefined;

    document.body.classList.add(CLASSE_TRAVA_ROLAGEM);
    return () => document.body.classList.remove(CLASSE_TRAVA_ROLAGEM);
  }, [aberto]);

  // Foco: ao abrir vai para o botão de fechar; ao fechar volta ao hambúrguer,
  // para quem navega por teclado não ser jogado ao início do documento.
  useEffect(() => {
    if (aberto) {
      jaAbriu.current = true;
      fecharRef.current?.focus();
      return;
    }

    // Fechou: só recupera o foco se ele ficou órfão (o item clicado saiu do
    // fluxo de tabulação). Se o usuário já está em outro elemento, não roubar.
    if (jaAbriu.current && document.activeElement === document.body) {
      botaoRef.current?.focus();
    }
  }, [aberto]);

  // Acompanha presença/altura do PendenciasBanner. Ele monta só depois das
  // consultas de status e some quando não há pendência, então não basta medir
  // uma vez: o MutationObserver (sem `subtree`, que dispararia a cada render de
  // tela) vê ele entrar/sair e o ResizeObserver acompanha a mudança de altura.
  useEffect(() => {
    if (!visivel) return undefined;

    const medir = () => {
      const banner = document.querySelector<HTMLElement>('[data-pendencias-banner]');
      setOffsetTopo(banner ? banner.offsetHeight : 0);
      return banner;
    };

    const observadorAltura =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => medir()) : null;

    const remedir = () => {
      const banner = medir();
      if (observadorAltura) {
        observadorAltura.disconnect();
        if (banner) observadorAltura.observe(banner);
      }
    };

    remedir();

    const raiz = document.getElementById('root');
    const observadorDom =
      raiz && typeof MutationObserver !== 'undefined' ? new MutationObserver(remedir) : null;
    if (raiz && observadorDom) observadorDom.observe(raiz, { childList: true });

    return () => {
      if (observadorDom) observadorDom.disconnect();
      if (observadorAltura) observadorAltura.disconnect();
    };
  }, [visivel, location.pathname]);

  const irPara = (opcao: OpcaoMenu) => {
    // Mesmo efeito colateral da Home: fluxo novo de cotação começa limpo.
    if (opcao.limparCotacao) {
      dispatch(clearCotacaoData());
    }
    // Idem para adesão (at-38) — a Home e o drawer partem da MESMA lista, então
    // tratar a flag só num dos dois faria o começo do fluxo depender de por
    // onde o consultor entrou.
    if (opcao.limparAdesao) {
      iniciarNovaAdesao(dispatch);
    }
    navigate(opcao.rota);
    fechar();
  };

  const irParaInicio = () => {
    navigate(HOME());
    fechar();
  };

  const sair = () => {
    fechar();
    // Logout manual (botão "Sair") vai sem motivo — igual ao Header (at-22).
    void performLogout();
  };

  if (!visivel) return null;

  /*
   * Tela com AppShell: nada sobe daqui.
   *
   * ⚠️ **Até a at-56 o sino ainda vinha deste caminho**, e era isso que o fazia
   * flutuar por cima da ação da tela (o "Nova Adesão +" de `/adesoes`). Agora
   * quem o monta nas rotas migradas é o `ShellSaac`, **dentro** do cabeçalho —
   * então devolver `<SinoNovidades>` aqui produziria DOIS sinos, o mesmo cuidado
   * que este componente já tinha com o hambúrguer.
   */
  if (shellAssumeNavegacao) return null;

  return (
    <>
      <button
        type="button"
        ref={botaoRef}
        className="nav-global-hamburguer"
        style={{ top: `calc(${offsetTopo}px + var(--nav-global-margem-topo))` }}
        onClick={() => setAberto(true)}
        aria-label="Abrir menu de navegação"
        aria-expanded={aberto}
        aria-controls="nav-global-painel"
      >
        <span className="nav-global-hamburguer-traco" aria-hidden="true" />
        <span className="nav-global-hamburguer-traco" aria-hidden="true" />
        <span className="nav-global-hamburguer-traco" aria-hidden="true" />
      </button>

      {/*
        Sino de novidades (3ª entrega da at-53). Montado AQUI, e não no App, por
        duas razões: herda a mesma regra de visibilidade do hambúrguer (some sem
        token e na tela de login) e reaproveita a medição do PendenciasBanner —
        os dois dividem a faixa do topo, e medir duas vezes divergiria.
      */}
      <SinoNovidades offsetTopo={offsetTopo} />

      {aberto && <div className="nav-global-overlay" onClick={fechar} role="presentation" />}

      <aside
        id="nav-global-painel"
        className={`nav-global-painel${aberto ? ' nav-global-painel-aberto' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Menu de navegação"
        aria-hidden={!aberto}
      >
        <div className="nav-global-topo">
          <img className="nav-global-logo" src={logo} alt="Atual Proteção Veicular" />
          <button
            type="button"
            ref={fecharRef}
            className="nav-global-fechar"
            onClick={fechar}
            aria-label="Fechar menu"
            tabIndex={aberto ? 0 : -1}
          >
            &times;
          </button>
        </div>

        <nav className="nav-global-lista" aria-label="Navegação principal">
          <button
            type="button"
            className={`nav-global-item${inicioAtivo ? ' nav-global-item-ativo' : ''}`}
            onClick={irParaInicio}
            tabIndex={aberto ? 0 : -1}
            aria-current={inicioAtivo ? 'page' : undefined}
          >
            <span className="nav-global-item-icone nav-global-item-icone-inicio">
              <IconeInicio />
            </span>
            <span className="nav-global-item-rotulo">Início</span>
          </button>

          {opcoes.map((opcao) => {
            const ativo = opcao.id === idAtivo;
            return (
              <button
                key={opcao.id}
                type="button"
                className={`nav-global-item${ativo ? ' nav-global-item-ativo' : ''}`}
                onClick={() => irPara(opcao)}
                tabIndex={aberto ? 0 : -1}
                aria-current={ativo ? 'page' : undefined}
              >
                <span className="nav-global-item-icone" aria-hidden="true">
                  <img className="nav-global-item-icone-img" src={opcao.icone} alt="" />
                </span>
                <span className="nav-global-item-rotulo">{opcao.titulo}</span>
              </button>
            );
          })}
        </nav>

        <div className="nav-global-rodape">
          <button
            type="button"
            className="nav-global-sair"
            onClick={sair}
            tabIndex={aberto ? 0 : -1}
          >
            Sair
          </button>
        </div>
      </aside>
    </>
  );
};

export default NavGlobal;
